import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Copy, Share2, Gift, Users, IndianRupee, CheckCircle, ExternalLink } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import WithdrawalRequestForm from '../components/WithdrawalRequestForm';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');
  const referralCode = localStorage.getItem('userReferralCode');

  useEffect(() => {
    if (!userId) {
      navigate('/user-login');
      return;
    }
    fetchReferralStats();
  }, [userId]);

  const fetchReferralStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/referral/stats/${userId}`);
      setStats(response.data);
      // Update localStorage with latest earnings
      localStorage.setItem('userEarnings', response.data.total_earnings);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load referral stats",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getReferralLink = () => {
    return `${window.location.origin}/user-login?ref=${referralCode}`;
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    toast({
      title: "Copied!",
      description: "Referral code copied to clipboard"
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(getReferralLink());
    toast({
      title: "Copied!",
      description: "Referral link copied to clipboard"
    });
  };

  const shareReferral = () => {
    const text = `Join me on JEE College Predictor! Use my referral code ${referralCode} and get exclusive benefits. ${getReferralLink()}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'JEE College Predictor Referral',
        text: text
      });
    } else {
      copyReferralLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your referral stats...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/')} className="gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Referral Dashboard</h1>
          <p className="text-slate-600">Earn ₹200 for every friend who joins using your referral code!</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="shadow-lg border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Earnings</p>
                  <p className="text-3xl font-bold text-green-600 flex items-center">
                    <IndianRupee className="w-6 h-6" />
                    {stats?.total_earnings || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Gift className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Referrals</p>
                  <p className="text-3xl font-bold text-blue-600">{stats?.referrals_count || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Earning Per Referral</p>
                  <p className="text-3xl font-bold text-purple-600 flex items-center">
                    <IndianRupee className="w-6 h-6" />
                    200
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Withdrawal Request Form */}
        <div className="mb-8">
          <WithdrawalRequestForm 
            userId={userId} 
            currentEarnings={stats?.total_earnings || 0}
            onSuccess={fetchReferralStats}
          />
        </div>

        {/* Referral Code Card */}
        <Card className="shadow-xl mb-8 bg-gradient-to-br from-blue-50 to-cyan-50">
          <CardHeader>
            <CardTitle>Your Referral Code</CardTitle>
            <CardDescription>Share this code with friends to earn rewards</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-white border-2 border-blue-200 rounded-lg p-4">
                  <p className="text-2xl font-bold text-center text-blue-600 tracking-wider">
                    {referralCode}
                  </p>
                </div>
                <Button
                  onClick={copyReferralCode}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {copied ? <CheckCircle className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-slate-600 mb-2">Referral Link:</p>
                <div className="flex items-center gap-2">
                  <Input
                    value={getReferralLink()}
                    readOnly
                    className="flex-1 bg-white"
                  />
                  <Button
                    onClick={copyReferralLink}
                    variant="outline"
                    size="sm"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={shareReferral}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Referral
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card className="shadow-lg mb-8">
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Share Your Code</h3>
                  <p className="text-slate-600">Share your unique referral code with friends and family</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Friend Signs Up</h3>
                  <p className="text-slate-600">Your friend registers using your referral code</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">They Make Payment</h3>
                  <p className="text-slate-600">When your friend completes their first payment</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-bold flex-shrink-0">
                  4
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">You Earn ₹200</h3>
                  <p className="text-slate-600">₹200 is automatically credited to your account!</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referrals List */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Your Referrals ({stats?.referrals_count || 0})</CardTitle>
            <CardDescription>People who joined using your referral code</CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.referrals && stats.referrals.length > 0 ? (
              <div className="space-y-3">
                {stats.referrals.map((referral, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-semibold text-slate-900">{referral.name}</p>
                      <p className="text-sm text-slate-600">{referral.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">
                        {new Date(referral.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 mb-2">No referrals yet</p>
                <p className="text-sm text-slate-500">Start sharing your referral code to earn rewards!</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default ReferralDashboard;
